// In-memory medical store for the mock interceptor (VITE_USE_MOCKS=true) and
// the tests. Shapes mirror erp-core internal/medical/wire.go byte for byte;
// the plan card and tooth states are composed here the way erp-core's
// ComposeTeeth / Summarize do (server-side logic emulated by the MOCK — the
// screens never compute them). Sample values are shapes, never seed data.

import type {
  Attachment,
  Booking,
  Chart,
  Drug,
  Numbering,
  Plan,
  Prescription,
  Procedure,
  ProcedureStatus,
  Tooth,
  ToothConditionKind,
} from '../types';

export const MOCK_PATIENT_ID = '7d0f8a52-3c1e-4b9a-9f2d-2a6b1c4e5f01';
/** A clinic customer with no medical history (first-use states). */
export const MOCK_NEW_PATIENT_ID = '7d0f8a52-3c1e-4b9a-9f2d-2a6b1c4e5f02';
export const MOCK_APPT_TODAY = 'a1b2c3d4-0000-4000-8000-000000000915';
export const MOCK_APPT_NEXT = 'a1b2c3d4-0000-4000-8000-000000000922';
export const MOCK_NODE_ID = '00000000-0000-4000-8000-0000000000c1';
export const MOCK_BRANCH_ID = '00000000-0000-4000-8000-0000000000b1';
const STAFF_SARA = '0c4f7f0e-5b8e-4f43-8f1a-4d1f3c2b1a00';

export type MedicalMockScenario =
  /** reads + writes answer normally */
  | 'default'
  /** every medical/BFF read fails at the network layer */
  | 'down'
  /** writes answer 409 (procedure_changed / prescription list changed) */
  | 'conflict'
  /** finish-visit answers the journal's 409 period_closed */
  | 'period_closed'
  /** finish-visit route absent (no Biller wired — today's production) */
  | 'no_biller';

interface Customer {
  id: string;
  name: string;
  phone: string;
  gender?: 'female' | 'male';
  platform_user_id?: string;
}

interface Condition {
  fdi: number;
  condition: ToothConditionKind;
  recorded_on: string;
  staff_name: string;
  note: string;
}

interface State {
  scenario: MedicalMockScenario;
  customers: Customer[];
  allergies: Record<string, string[]>;
  notes: Record<string, string>;
  conditions: Record<string, Condition[]>;
  procedures: Record<string, Procedure[]>;
  prescriptions: Record<string, Prescription[]>;
  attachments: Record<string, Attachment[]>;
  formulary: Drug[];
  bookings: Record<string, Booking>;
  rxSeq: number;
  invoiceSeq: number;
  idSeq: number;
}

const ts = '2026-09-15T08:00:00Z';

function proc(p: Partial<Procedure> & Pick<Procedure, 'id' | 'tooth_fdi' | 'name' | 'status' | 'price_minor'>): Procedure {
  return {
    branch_id: MOCK_BRANCH_ID,
    customer_id: MOCK_PATIENT_ID,
    tooth_universal: universal(p.tooth_fdi),
    detail: '',
    scheduled_on: null,
    appointment_id: null,
    done_on: null,
    staff_id: STAFF_SARA,
    staff_name: 'د. سارة',
    currency: 'IQD',
    invoice_id: null,
    invoice_no: null,
    note: '',
    node_id: MOCK_NODE_ID,
    sync_version: 1,
    created_at: ts,
    updated_at: ts,
    ...p,
  };
}

function drug(id: string, name: string, strength: string, form: string, dosage: string, duration: string): Drug {
  return { id, name, strength, form, default_dosage_text: dosage, default_duration_text: duration, active: true, sync_version: 1, updated_at: ts };
}

function initial(): State {
  return {
    scenario: 'default',
    customers: [
      { id: MOCK_PATIENT_ID, name: 'زهراء الموسوي', phone: '+9647701234567', gender: 'female', platform_user_id: '5e2b7c1a-9d3f-4a8e-b6c2-1f0e9d8c7b6a' },
      { id: MOCK_NEW_PATIENT_ID, name: 'علي حسين الكعبي', phone: '+9647809876543', gender: 'male' },
    ],
    allergies: { [MOCK_PATIENT_ID]: ['بنسلين'] },
    notes: {},
    conditions: {
      [MOCK_PATIENT_ID]: [
        { fdi: 28, condition: 'missing', recorded_on: '2026-09-02', staff_name: 'د. سارة', note: '' },
        { fdi: 48, condition: 'missing', recorded_on: '2026-09-02', staff_name: 'د. سارة', note: '' },
      ],
    },
    procedures: {
      [MOCK_PATIENT_ID]: [
        proc({ id: 'b0e1c2d3-0000-4000-8000-000000000016', tooth_fdi: 16, name: 'حشوة ضوئية', detail: 'سطح إطباقي', status: 'done', done_on: '2026-09-02', scheduled_on: '2026-09-02', price_minor: 75000, invoice_id: '9f000000-0000-4000-8000-000000000041', invoice_no: '#0041' }),
        proc({ id: 'b0e1c2d3-0000-4000-8000-000000000036', tooth_fdi: 36, name: 'علاج جذور', detail: '٣ قنوات', status: 'done', done_on: '2026-09-08', scheduled_on: '2026-09-08', price_minor: 200000, invoice_id: '9f000000-0000-4000-8000-000000000049', invoice_no: '#0049' }),
        proc({ id: 'b0e1c2d3-0000-4000-8000-000000000026', tooth_fdi: 26, name: 'تاج زيركون', status: 'in_progress', scheduled_on: '2026-09-15', appointment_id: MOCK_APPT_TODAY, price_minor: 250000 }),
        proc({ id: 'b0e1c2d3-0000-4000-8000-000000000024', tooth_fdi: 24, name: 'حشوة ضوئية', status: 'planned', scheduled_on: '2026-09-22', appointment_id: MOCK_APPT_NEXT, price_minor: 75000 }),
      ],
    },
    prescriptions: {
      [MOCK_PATIENT_ID]: [
        {
          id: 'c7000000-0000-4000-8000-000000000013',
          number: '#RX-0013',
          no: 13,
          branch_id: MOCK_BRANCH_ID,
          customer_id: MOCK_PATIENT_ID,
          prescriber_id: STAFF_SARA,
          prescriber_name: 'د. سارة الحسيني',
          appointment_id: null,
          is_financial: false,
          lines: [
            { drug_id: 'd1000000-0000-4000-8000-000000000003', drug_name: 'Clindamycin', strength: '300 mg', dosage_text: 'كبسولة كل ٦ ساعات', duration_text: '٥ أيام' },
            { drug_id: 'd1000000-0000-4000-8000-000000000004', drug_name: 'Paracetamol', strength: '500 mg', dosage_text: 'قرص عند الألم، أربع مرات يومياً بحد أقصى', duration_text: '٣ أيام' },
          ],
          instructions: 'المراجعة بعد أسبوع لإكمال علاج الجذور.',
          allergies_snapshot: ['بنسلين'],
          issued_at: '2026-09-08T07:40:00Z',
          printed_at: '2026-09-08T07:41:00Z',
          sent_at: null,
          node_id: MOCK_NODE_ID,
          sync_version: 1,
        },
      ],
    },
    attachments: {
      [MOCK_PATIENT_ID]: [
        att('e1000000-0000-4000-8000-000000000001', 'أشعة بانورامية.jpg', 'image/jpeg', 2_202_010, '2026-09-02T07:10:00Z', 'د. سارة'),
        att('e1000000-0000-4000-8000-000000000002', 'أشعة ذروية السن ٣٦.jpg', 'image/jpeg', 1_468_006, '2026-09-08T07:20:00Z', 'د. سارة'),
        att('e1000000-0000-4000-8000-000000000003', 'موافقة علاج الجذور.pdf', 'application/pdf', 734_003, '2026-09-08T07:25:00Z', 'سُهى'),
      ],
    },
    formulary: [
      drug('d1000000-0000-4000-8000-000000000001', 'Ibuprofen', '400 mg', 'قرص', 'قرص كل ٨ ساعات بعد الأكل', '٣ أيام'),
      drug('d1000000-0000-4000-8000-000000000002', 'Chlorhexidine', '0.12%', 'مضمضة', 'مضمضة مرتين يومياً', '٧ أيام'),
      drug('d1000000-0000-4000-8000-000000000003', 'Clindamycin', '300 mg', 'كبسولة', 'كبسولة كل ٦ ساعات', '٥ أيام'),
      drug('d1000000-0000-4000-8000-000000000004', 'Paracetamol', '500 mg', 'قرص', 'قرص عند الألم، أربع مرات يومياً بحد أقصى', '٣ أيام'),
      drug('d1000000-0000-4000-8000-000000000005', 'Amoxicillin', '500 mg', 'كبسولة', 'كبسولة كل ٨ ساعات', '٧ أيام'),
    ],
    bookings: {
      [MOCK_APPT_TODAY]: {
        id: MOCK_APPT_TODAY,
        customer_name: 'زهراء الموسوي',
        customer_phone: '+9647701234567',
        service_name: 'تاج زيركون',
        staff_name: 'د. سارة',
        start_at: '2026-09-15T08:05:00Z',
        status: 'confirmed',
        notes: 'تحضير السن ٢٦ للتاج، أخذ الطبعة، تاج مؤقت. المراجعة بعد ١٠ أيام لتركيب الزيركون.',
      },
      [MOCK_APPT_NEXT]: {
        id: MOCK_APPT_NEXT,
        customer_name: 'زهراء الموسوي',
        customer_phone: '+9647701234567',
        service_name: 'حشوة ضوئية',
        staff_name: 'د. سارة',
        start_at: '2026-09-22T07:00:00Z',
        status: 'confirmed',
      },
    },
    rxSeq: 13,
    invoiceSeq: 49,
    idSeq: 1,
  };
}

function att(id: string, file_name: string, mime: string, size_bytes: number, at: string, by: string): Attachment {
  return { id, file_name, mime, size_bytes, state: 'ready', uploaded_at: at, uploaded_by: STAFF_SARA, uploaded_by_name: by, ready_at: at, sync_version: 1 };
}

let state: State = initial();

export function resetMedicalMocks(): void {
  state = initial();
}

export function setMedicalMockScenario(s: MedicalMockScenario): void {
  state.scenario = s;
}

export function medicalMock(): State {
  return state;
}

export function nextId(prefix: string): string {
  state.idSeq += 1;
  return `${prefix}000000-0000-4000-8000-${String(state.idSeq).padStart(12, '0')}`;
}

// ------------------------------------------------- server emulation --

/** FDI → Universal (erp-core teeth.go). */
export function universal(fdi: number): number {
  const q = Math.floor(fdi / 10);
  const t = fdi % 10;
  if (q < 1 || q > 4 || t < 1 || t > 8) return 0;
  if (q === 1) return 9 - t;
  if (q === 2) return 8 + t;
  if (q === 3) return 25 - t;
  return 24 + t;
}

/** erp-core AllFDI order: 18…11, 21…28, 38…31, 41…48. */
const ALL_FDI: number[] = [
  ...[8, 7, 6, 5, 4, 3, 2, 1].map((t) => 10 + t),
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((t) => 20 + t),
  ...[8, 7, 6, 5, 4, 3, 2, 1].map((t) => 30 + t),
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((t) => 40 + t),
];

function composeTeeth(customerId: string, numbering: Numbering): Tooth[] {
  const conds = state.conditions[customerId] ?? [];
  const procs = (state.procedures[customerId] ?? []).filter((p) => p.status !== 'cancelled' && p.tooth_fdi !== 0);
  return ALL_FDI.map((fdi) => {
    const c = [...conds].reverse().find((x) => x.fdi === fdi);
    const mine = procs.filter((p) => p.tooth_fdi === fdi);
    const has = (s: ProcedureStatus) => mine.some((p) => p.status === s);
    const state: Tooth['state'] =
      c?.condition === 'missing' ? 'missing' : has('in_progress') ? 'in_progress' : has('planned') ? 'planned' : has('done') ? 'done' : 'none';
    return {
      fdi,
      universal_no: universal(fdi),
      display_no: numbering === 'universal' ? universal(fdi) : fdi,
      quadrant: Math.floor(fdi / 10),
      state,
      condition: c?.condition ?? null,
      condition_note: c?.note ?? '',
      condition_on: c?.recorded_on ?? null,
      condition_staff_name: c?.staff_name ?? '',
      procedure_ids: mine.map((p) => p.id),
    };
  });
}

function summarize(procs: Procedure[]): Plan {
  const live = procs.filter((p) => p.status !== 'cancelled');
  const plan: Plan = { state: 'none', total_count: 0, done_count: 0, in_progress_count: 0, planned_count: 0, plan_total_minor: 0, billed_done_minor: 0, remaining_minor: 0, currency: 'IQD' };
  for (const p of live) {
    plan.total_count += 1;
    plan.plan_total_minor += p.price_minor;
    if (p.status === 'done') {
      plan.done_count += 1;
      if (p.invoice_id) plan.billed_done_minor += p.price_minor;
    } else if (p.status === 'in_progress') plan.in_progress_count += 1;
    else if (p.status === 'planned') plan.planned_count += 1;
  }
  plan.remaining_minor = plan.plan_total_minor - plan.billed_done_minor;
  plan.state = plan.total_count === 0 ? 'none' : plan.done_count === plan.total_count ? 'complete' : 'in_progress';
  return plan;
}

export function chartFor(customerId: string, numbering: Numbering): Chart {
  const procs = state.procedures[customerId] ?? [];
  return {
    customer_id: customerId,
    numbering,
    allergies: state.allergies[customerId] ?? [],
    notes: state.notes[customerId] ?? '',
    plan: summarize(procs),
    teeth: composeTeeth(customerId, numbering),
    procedures: procs,
    attachments_count: (state.attachments[customerId] ?? []).filter((a) => a.state === 'ready').length,
  };
}

export function knownCustomer(id: string): Customer | undefined {
  return state.customers.find((c) => c.id === id);
}
